import React, { useContext, useLayoutEffect, useState } from "react";
import { DispatchManifestStopDetailStatus } from "app/data/city-delivery/dispatch-board/models";
import { ReactComponent as Arrow } from "images/arrow-down_gray.svg";
import { useUnassignedStopNestExpandSync } from "../unassignedStopNestExpandContext";
import { PickupAssignmentMapSyncContext } from "../pickupMapSyncContext";
import type {
  UnassignedGroupByMode,
  UnassignedPickup,
  UnassignedStopGroup,
} from "../unassignedPickupTypes";
import { PickupFreightItemsBlock } from "../../../common/pickup-details/pickupFreightItemsBlock";
import { sanitizePickupDisplayText } from "../sanitizePickupDisplay";
import { formatCollapsedPickupWindow } from "./pickupGroupWindowDisplay";
import {
  WorkTypeExpandedCard,
  WorkTypeTagStackWithHover,
} from "../components/PickupAssignmentWorkloadTags";
import "./pickupGroupCard.scss";

const groupStatusClass: Record<DispatchManifestStopDetailStatus, string> = {
  [DispatchManifestStopDetailStatus.COMPLETED]: "pa-pickup-group__tag--completed",
  [DispatchManifestStopDetailStatus.IN_PROGRESS]: "pa-pickup-group__tag--in-progress",
  [DispatchManifestStopDetailStatus.PENDING]: "pa-pickup-group__tag--pending",
  [DispatchManifestStopDetailStatus.EXCEPTIONS]: "pa-pickup-group__tag--exceptions",
};

function titleCaseWords(s: string): string {
  return s
    .split("_")
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");
}

function groupStatusLabel(s: DispatchManifestStopDetailStatus): string {
  return titleCaseWords(s);
}

type Props = {
  task: UnassignedStopGroup;
  groupBy: UnassignedGroupByMode;
  onSelectPickup: (pickupNumber: string) => void;
  selectedPickupIds: Set<string>;
  onTogglePickupSelection: (pickupId: string) => void;
  onStopMapFocus?: (stopId: string) => void;
};

function UnassignedPickupRow({
  pickup,
  onSelectPickup,
  selected,
  onToggleSelection,
}: {
  pickup: UnassignedPickup;
  onSelectPickup: (pickupNumber: string) => void;
  selected: boolean;
  onToggleSelection: () => void;
}) {
  const [open, setOpen] = useState(false);
  const restSummary = ` · ${sanitizePickupDisplayText(pickup.shipperName)} → ${sanitizePickupDisplayText(
    pickup.consigneeSummaryLabel
  )} · Rolls ${pickup.rollsUnits} · Pallets ${pickup.palletsUnits} · ${pickup.totalItemsWeight} lbs`;

  return (
    <li className="pa-pickup-group__item pa-pickup-group__item--compact">
      <div className="pa-pickup-group__pickup-select-row">
        <label className="pa-pickup-group__pickup-check">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelection}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Select pickup ${pickup.puNumber}`}
          />
        </label>
        <div className="pa-pickup-group__pickup-select-body">
          <div
            role="button"
            tabIndex={0}
            className={`pa-pickup-group__pickup-head ${!open ? "pa-pickup-group__pickup-head--single-line" : ""}`}
            onClick={() => setOpen((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setOpen((v) => !v);
              }
            }}
            aria-expanded={open}
            aria-label={open ? "Collapse pickup details" : "Expand pickup details"}
          >
            <span className="pa-pickup-group__pickup-expand" aria-hidden>
              <Arrow
                className="pa-pickup-group__pickup-chevron"
                width={8}
                height={12}
                color="#828282"
                style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
              />
            </span>
            <div className="pa-pickup-group__pickup-summary">
              <button
                type="button"
                className="pa-pickup-group__link-btn pa-pickup-group__link-btn--inline"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectPickup(pickup.puNumber);
                }}
              >
                {pickup.puNumber}
              </button>
              <span className="pa-pickup-group__pickup-summary-rest">{restSummary}</span>
            </div>
          </div>
          {open && (
            <div className="pa-pickup-group__pickup-details">
              <div className="pa-pickup-group__row">
                <span className="pa-pickup-group__label">Pickup #</span>
                <button
                  type="button"
                  className="pa-pickup-group__link-btn"
                  onClick={() => onSelectPickup(pickup.puNumber)}
                >
                  {pickup.puNumber}
                </button>
              </div>
              <div className="pa-pickup-group__row">
                <span className="pa-pickup-group__label">Pickup window</span>
                <span>{sanitizePickupDisplayText(pickup.pickupWindowLine)}</span>
              </div>
              <div className="pa-pickup-group__row pa-pickup-group__row--stack">
                <span className="pa-pickup-group__label">Shipper</span>
                <div className="pa-pickup-group__value-stack">
                  <div>{sanitizePickupDisplayText(pickup.shipperName)}</div>
                  <div>{sanitizePickupDisplayText(pickup.shipperContactLine)}</div>
                </div>
              </div>
              <div className="pa-pickup-group__row pa-pickup-group__row--stack">
                <span className="pa-pickup-group__label">Consignee</span>
                <div className="pa-pickup-group__value-stack">
                  <div>{sanitizePickupDisplayText(pickup.consigneeName)}</div>
                  <div>{sanitizePickupDisplayText(pickup.consigneeAddressLine)}</div>
                  <div>{sanitizePickupDisplayText(pickup.consigneeContactLine)}</div>
                </div>
              </div>
              <div className="pa-pickup-group__detail-section">
                <div className="pa-pickup-group__detail-section-title">Items</div>
                {pickup.itemLines.length === 0 ? (
                  <span className="pa-pickup-group__empty-items">—</span>
                ) : (
                  <div className="pa-pickup-group__freight-items">
                    <PickupFreightItemsBlock items={pickup.itemLines} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

export const UnassignedPickupStopGroup: React.FC<Props> = ({
  task,
  groupBy,
  onSelectPickup,
  selectedPickupIds,
  onTogglePickupSelection,
  onStopMapFocus,
}) => {
  const mapSyncCtx = useContext(PickupAssignmentMapSyncContext);

  const [open, setOpen] = useUnassignedStopNestExpandSync();

  const focusStopVersion = mapSyncCtx?.focusStopVersion;
  const focusStopId = mapSyncCtx?.focusStopId;
  useLayoutEffect(() => {
    if (!focusStopId || focusStopId !== task.id) return;
    setOpen(true);
  }, [focusStopVersion, focusStopId, task.id, setOpen]);

  const first = task.pickups[0];
  const addressLine = first?.address ?? "—";
  const nameLine = sanitizePickupDisplayText(first?.shipperName ?? "—");
  const primaryHeadlineLine = groupBy === "shipper_name" ? nameLine : addressLine;
  const windowCollapsed = first
    ? formatCollapsedPickupWindow(first.pickupDateForTiming, first.timeRange)
    : "—";
  const collapsedHeadline = `${primaryHeadlineLine} · ${windowCollapsed}`;
  const pickupCount = task.pickups.length;

  return (
    <div className={`pa-pickup-group${open ? " pa-pickup-group--expanded" : ""}`}>
      <button
        type="button"
        className={`pa-pickup-group__head ${!open ? "pa-pickup-group__head--single-line" : ""}`}
        onClick={() => {
          setOpen((v) => {
            if (!v) {
              onStopMapFocus?.(task.id);
            }
            return !v;
          });
        }}
        aria-expanded={open}
      >
        <Arrow
          className="pa-pickup-group__chevron"
          width={8}
          height={12}
          color="#828282"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
        <div className="pa-pickup-group__head-main">
          {!open ? (
            <>
              <div className="pa-pickup-group__collapsed-row">
                <span className="pa-pickup-group__address pa-pickup-group__address--collapsed">
                  {collapsedHeadline}
                </span>
                <span className="pa-pickup-group__stat-tags">
                  <WorkTypeTagStackWithHover variant="pickup" completed={0} total={pickupCount} />
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="pa-pickup-group__line1">
                <span className="pa-pickup-group__address">{primaryHeadlineLine}</span>
                <span
                  className={`pa-pickup-group__tag ${groupStatusClass[task.groupStopStatus] ?? ""}`}
                >
                  {groupStatusLabel(task.groupStopStatus)}
                </span>
              </div>
              {groupBy === "shipper_name" && first?.address ? (
                <div className="pa-pickup-group__group-subheading">{first.address}</div>
              ) : null}
              <div className="pa-pickup-group__window-count-row">
                <span className="pa-pickup-group__expanded-window">
                  {sanitizePickupDisplayText(first?.pickupWindowLine ?? "—")}
                </span>
                <span className="pa-pickup-group__expanded-stat-tags">
                  <WorkTypeExpandedCard
                    variant="pickup"
                    completed={0}
                    total={pickupCount}
                    className="pa-work-tag-stack--expanded-inline"
                  />
                </span>
              </div>
            </>
          )}
        </div>
      </button>

      {open && (
        <ul className="pa-pickup-group__list">
          {task.pickups.map((pickup) => (
            <UnassignedPickupRow
              key={pickup.id}
              pickup={pickup}
              onSelectPickup={onSelectPickup}
              selected={selectedPickupIds.has(pickup.id)}
              onToggleSelection={() => onTogglePickupSelection(pickup.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
};
